import type { Currency } from 'src/types/listing';
import type { ChainFamily } from 'src/blockchain';
import type { ApiChainToken, EscrowVertical, ApiAvailableChain } from 'src/types/chain';

import { FAMILY_LABEL, ENABLED_FAMILIES } from 'src/blockchain';

/**
 * Whether the escrow action of a listing — Book on a rental, "Buy with delivery" on a sale — may
 * be offered to this buyer (app#176, BUG-01 of the 31.08 storefront report).
 *
 * api decides the chain of a NEW deal by two facts and nothing about the buyer
 * (`resolveEscrowChain`): the chain is open for the vertical (`escrowVerticals` in
 * `GET /buyer/chains`) and it carries a token for the listing's currency. The buyer then pays on
 * that chain, so the third fact is theirs: a wallet of that chain's family must be linked to the
 * account — and this build must carry that family's wallet stack, or the payment screen has nothing
 * to connect. Offering the action without the third fact sends the buyer to a payment they cannot
 * sign, and the api's refusal reaches them with no explanation.
 *
 * Nothing here names a chain. The notice reads what the registry says today; when the follow-up
 * track opens TRON for v3 escrow again, the registry changes and the notice goes away by itself
 * (Tim, 03.09: "build nothing specifically for Base").
 *
 * Unknown never reads as a refusal: while the registry is loading, or against an api too old to
 * report `escrowVerticals`, or when the linked wallets are not known, the action stays offered as
 * it always was and the server has the last word.
 */
export type EscrowOffer =
  /** Offered — or not decidable yet, which keeps the pre-existing behaviour. */
  | { kind: 'offered' }
  /** A network hosts the deal, but the buyer has no wallet of its family linked. */
  | { kind: 'no-wallet'; hosting: ApiAvailableChain[]; missingFamilies: ChainFamily[] }
  /** No enabled network takes new deals of this vertical in this currency. */
  | { kind: 'not-hosted' };

export interface EscrowOfferParams {
  /** `GET /buyer/chains`; `undefined` while it is still loading. */
  buyerChains: ApiAvailableChain[] | undefined;
  vertical: EscrowVertical;
  listingCurrency: Currency;
  /**
   * Families the buyer's account has a wallet in. `undefined` means not known — the factor is then
   * skipped rather than read as "none", the same courtesy `resolvePurchaseChainOptions` extends.
   */
  linkedFamilies: ChainFamily[] | undefined;
  /** Families whose wallet stack this build carries; a parameter so the rule is testable. */
  buildFamilies?: ChainFamily[];
}

export const resolveEscrowOffer = ({
  buyerChains,
  vertical,
  listingCurrency,
  linkedFamilies,
  buildFamilies = ENABLED_FAMILIES,
}: EscrowOfferParams): EscrowOffer => {
  // Not decidable: keep offering, the server refuses if it must.
  if (!buyerChains?.length || buyerChains.some((chain) => chain.escrowVerticals === undefined)) {
    return { kind: 'offered' };
  }

  const hosting = hostingChains(buyerChains, vertical, listingCurrency);

  if (hosting.length === 0) {
    return { kind: 'not-hosted' };
  }

  if (linkedFamilies === undefined) {
    return { kind: 'offered' };
  }

  const payable = hosting.some(
    (chain) => linkedFamilies.includes(chain.family) && buildFamilies.includes(chain.family)
  );

  if (payable) {
    return { kind: 'offered' };
  }

  const missingFamilies = [...new Set(hosting.map((chain) => chain.family))].filter((family) =>
    buildFamilies.includes(family)
  );

  return { kind: 'no-wallet', hosting, missingFamilies };
};

/**
 * The networks a NEW deal of this vertical could be hosted on for a listing of this currency — the
 * two facts api reads (`resolveEscrowChain`): open for the vertical, and carrying a token for the
 * currency. In registry order, which is the order api picks in.
 */
const hostingChains = (
  buyerChains: ApiAvailableChain[],
  vertical: EscrowVertical,
  listingCurrency: Currency
): ApiAvailableChain[] =>
  buyerChains.filter(
    (chain) =>
      chain.escrowVerticals?.includes(vertical) && Boolean(chain.tokens[listingCurrency]?.address)
  );

/**
 * The token a NEW deal of this vertical settles in for a listing of this currency: the one of the
 * first network that hosts it, exactly as api will stamp the deal. This is where a screen takes
 * the decimals of a server quote from — the quote's lines are base units of this token, and a
 * USD car is paid in USDC while a euro car is paid in EURC; nothing in the build knows a token's
 * decimals on its own (the USDT build constants left with one rail M4b).
 *
 * `null` while the registry is loading or when no network hosts the deal: a screen then shows no
 * amount rather than one scaled by a guessed token. Against an api too old to say which verticals
 * a chain hosts, the same courtesy `resolveEscrowOffer` extends: the deal is not refused, so the
 * token is the one of the single network that carries the currency — several would be a guess.
 */
export const resolveEscrowToken = (
  buyerChains: ApiAvailableChain[] | undefined,
  vertical: EscrowVertical,
  listingCurrency: Currency | undefined
): ApiChainToken | null => {
  if (!buyerChains?.length || !listingCurrency) {
    return null;
  }

  if (buyerChains.some((chain) => chain.escrowVerticals === undefined)) {
    const carrying = buyerChains.filter((chain) => Boolean(chain.tokens[listingCurrency]?.address));

    return carrying.length === 1 ? (carrying[0].tokens[listingCurrency] as ApiChainToken) : null;
  }

  const [hosting] = hostingChains(buyerChains, vertical, listingCurrency);

  return hosting?.tokens[listingCurrency] ?? null;
};

/**
 * The decimals to print a server quote's base-unit lines with — or `null`, and then no amount is
 * printed. The quote names the token it is denominated in (`cryptoCurrency`, "USDC" / "EURC");
 * the registry knows that token's decimals through the network that hosts the deal. The two must
 * agree: a token found here under another symbol than the quote's would mean this build and api
 * disagree about where the deal settles, and a number scaled by the wrong token is worse than none.
 */
export const quoteDecimals = (
  quoteCurrency: string | undefined,
  buyerChains: ApiAvailableChain[] | undefined,
  vertical: EscrowVertical,
  listingCurrency: Currency | undefined
): number | null => {
  const token = resolveEscrowToken(buyerChains, vertical, listingCurrency);

  return token && quoteCurrency && token.symbol === quoteCurrency ? token.decimals : null;
};

const joinNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;

/**
 * The sentence shown in place of the action. `action` is what the buyer wanted to do ("Booking",
 * "Buying with delivery"); the rest is read from the offer, not from a chain name in this file.
 */
export const describeEscrowOffer = (
  offer: Exclude<EscrowOffer, { kind: 'offered' }>,
  action: string,
  listingCurrency: Currency
): string => {
  if (offer.kind === 'not-hosted') {
    return `${action} is not available right now: no network is open for new deals in ${listingCurrency}.`;
  }

  const networks = joinNames(offer.hosting.map((chain) => chain.displayName));

  if (offer.missingFamilies.length === 0) {
    return `${action} is not available in this app: this car is settled on ${networks}, which this app cannot pay on.`;
  }

  const families = joinNames(offer.missingFamilies.map((family) => FAMILY_LABEL[family]));

  return `${action} is not available with your linked wallets: this car is settled on ${networks}, and your account has no ${families} wallet. Link one to your account to continue.`;
};
